import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Grid,
    Text,
    Input,
    Button,
    Image,
    Flex,
    Portal,
    createListCollection,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { Field } from "../../Components/ui/field";
import { PasswordInput } from "../../Components/ui/password-input";
import { Select } from "@chakra-ui/react";
import { addWhitelabels } from "../../Redux/Action";
import { setDynamicTitle } from "../../Utils/dynamicTitleManager";

export const AddWhitelables = () => {
    // Use dynamic title management
    useEffect(() => {
        setDynamicTitle("Add Whitelabels");
    }, []);

    const initialFormState = {
        user_name: "",
        phone: "",
        password: "",
        domain_url: "",
        admin_domain_url: "",
        currency: "USD",
        group_name: "",
        white_label_type: null,
        light_mode_logo: null,
        dark_mode_logo: null,

    };

    const [formData, setFormData] = useState(initialFormState);
    const [formErrors, setFormErrors] = useState({});
    const [lightLogoPreview, setLightLogoPreview] = useState(null);
    const [darkLogoPreview, setDarkLogoPreview] = useState(null);

    const lightLogoInputRef = useRef(null);
    const darkLogoInputRef = useRef(null);
    const dispatch = useDispatch();

    const isValidDomain = (url) => {
        // Updated validation to work with domain without https:// prefix
        const domainRegex = /^([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
        return domainRegex.test(url);
    };


    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "phone") {

            if (!/^\d*$/.test(value)) return;

            if (value.length > 10) {
                setFormErrors((prev) => ({
                    ...prev,
                    [name]: "Phone number should not be more than 10 digits",
                }));


                setTimeout(() => {
                    setFormErrors((prev) => ({
                        ...prev,
                        [name]: "",
                    }));
                }, 1000);

                return;
            } else {

                setFormErrors((prev) => ({ ...prev, [name]: "" }));
            }

            setFormData((prev) => ({ ...prev, [name]: value }));
            return;
        }


        setFormData((prev) => ({ ...prev, [name]: value }));
        setFormErrors((prev) => ({ ...prev, [name]: "" }));

        if ((name === "domain_url" || name === "admin_domain_url") && value) {
            if (!isValidDomain(value)) {
                setFormErrors((prev) => ({
                    ...prev,
                    [name]: "Enter a valid domain (e.g., example.com)",
                }));
            }
        }
    };


    const handleCurrencyChange = (item) => {
        setFormData((prev) => ({ ...prev, currency: "USD" }));
        setFormErrors((prev) => ({ ...prev, currency: "" }));
    };

    const handleWhiteLabelTypeChange = (item) => {
        setFormData((prev) => ({ ...prev, white_label_type: item.value }));
        setFormErrors((prev) => ({ ...prev, white_label_type: "" }));
    };

    const handleLogoChange = (e, type) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFormData((prev) => ({ ...prev, [type]: file }));
        setFormErrors((prev) => ({ ...prev, [type]: "" }));

        const previewURL = URL.createObjectURL(file);
        if (type === "light_mode_logo") setLightLogoPreview(previewURL);
        else setDarkLogoPreview(previewURL);
    };

    const handleResetAll = () => {
        setFormData(initialFormState);
        setFormErrors({});
        setLightLogoPreview(null);
        setDarkLogoPreview(null);

        if (lightLogoInputRef.current) lightLogoInputRef.current.value = "";
        if (darkLogoInputRef.current) darkLogoInputRef.current.value = "";
    };

    const addWhitelabel = () => {
        const errors = {};
        const requiredFields = Object.keys(initialFormState);

        requiredFields.forEach((field) => {
            if (field === "group_name") return;

            const value = formData[field];
            const isFile = value instanceof File;
            const isEmpty = value === "" || value === null || (isFile && value.size === 0);

            if (isEmpty) {
                errors[field] = `Please ${field.includes("logo") ? "upload" : "fill"} the ${field.replace(/_/g, " ")}`;
            }
        });

        formData.createdBy = "masterAdmin";


        const mergedErrors = { ...formErrors, ...errors };
        setFormErrors(mergedErrors);

        const hasErrors = Object.values(mergedErrors).some((err) => err);
        if (hasErrors) return;

        // Prepend https:// to domain fields before submission
        const formDataWithProtocol = {
            ...formData,
            domain_url: formData.domain_url ? `https://${formData.domain_url}` : "",
            admin_domain_url: formData.admin_domain_url ? `https://${formData.admin_domain_url}` : ""
        };

        const localFormData = new FormData();
        Object.entries(formDataWithProtocol).forEach(([key, value]) => {
            if (value !== null) {
                if (key === "currency" || key === "white_label_type") {
                    localFormData.append(key, value[0]);
                } else {
                    localFormData.append(key, value);
                }
            }
        });

        

        dispatch(
            addWhitelabels({
                payload: localFormData,
                callback: (data) => {
                    if (data.meta.code === 200) {
                        setFormData(initialFormState);
                        setFormErrors({});
                        setLightLogoPreview(null);
                        setDarkLogoPreview(null);

                        if (lightLogoInputRef.current) lightLogoInputRef.current.value = "";
                        if (darkLogoInputRef.current) darkLogoInputRef.current.value = "";
                    } else {
                        console.log("Failed");
                    }
                },
            })
        );
    };


    const currencies = [

        { label: "US Dollar", value: "USD" },
    ];

    const whiteLabelType = [
        { label: "Challenge", value: "challenge" },
        { label: "B2C", value: "B2C" },
    ];
    useEffect(() => {
        if (!formData.currency && currencies.length === 1) {
            setFormData((prev) => ({ ...prev, currency: currencies[0].value }));
        }
    }, []);

    const hasErrors = Object.keys(formErrors).length > 0;

    return (
        <Box h="calc(100vh - 64px)" overflowY="auto" p={6}>
            <Text fontSize="lg" fontWeight="semibold" mb={4}>DETAILS</Text>
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                <Field label="USER NAME">
                    <Input
                        name="user_name"
                        value={formData.user_name}
                        onChange={handleInputChange}
                        isinvalid={!!formErrors.user_name}
                    />
                    {formErrors.user_name && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.user_name}</Text>}
                </Field>
                <Field label="PHONE">
                    <Input
                        type="number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        isinvalid={!!formErrors.phone}
                    />
                    {formErrors.phone && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.phone}</Text>}
                </Field>
                <Field label="PASSWORD">
                    <PasswordInput
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        isinvalid={!!formErrors.password}
                    />
                    {formErrors.password && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.password}</Text>}
                </Field>
            </Grid>

            <Text fontSize="lg" fontWeight="semibold" mt={8} mb={4}>DOMAIN</Text>
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                {["domain_url", "admin_domain_url", "group_name"].map((name, i) => (
                    <Field key={i} label={name.replace(/_/g, " ").toUpperCase()}>
                        {name === "domain_url" || name === "admin_domain_url" ? (
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <span style={{ 
                                    padding: '8px 12px', 
                                    border: '1px solid #E2E8F0', 
                                    borderRight: 'none', 
                                    borderRadius: '4px 0 0 4px', 
                                    backgroundColor: '#F7FAFC',
                                    color: '#4A5568',
                                    fontWeight: 'medium',
                                    whiteSpace: 'nowrap'
                                }}>
                                    https://
                                </span>
                                <Input
                                    name={name}
                                    value={formData[name]}
                                    onChange={handleInputChange}
                                    isinvalid={!!formErrors[name]}
                                    placeholder="example.com"
                                    style={{ 
                                        borderRadius: '0 4px 4px 0',
                                        borderLeft: 'none',
                                        flex: 1
                                    }}
                                />
                            </div>
                        ) : (
                            <Input
                                name={name}
                                value={formData[name]}
                                onChange={handleInputChange}
                                isinvalid={!!formErrors[name]}
                                placeholder={name === "group_name" ? "Optional" : ""}
                            />
                        )}
                        {formErrors[name] && <Text fontSize="xs" color="red.500" mt={1}>{formErrors[name]}</Text>}
                    </Field>
                ))}

                <Field label="CURRENCY">
                    <Select.Root
                        key={formData.currency}
                        collection={createListCollection({ items: currencies })}
                        onValueChange={handleCurrencyChange}
                        value={formData.currency ?? undefined}
                        width="100%"
                        positioning={{ sameWidth: true }}
                    >
                        <Select.HiddenSelect />
                        <Select.Control width="100%">
                            <Select.Trigger width="100%">
                                <Select.ValueText placeholder={currencies[0].value} />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator />
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                            <Select.Positioner>
                                <Select.Content width="100%">
                                    {currencies.map((c) => (
                                        <Select.Item item={c} key={c.value} width="100%">
                                            {c.label}
                                            <Select.ItemIndicator />
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Portal>
                    </Select.Root>
                    {formErrors.currency && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.currency}</Text>}
                </Field>

                <Field label="WHITE LABEL TYPE">
                    <Select.Root
                        key={formData.white_label_type}
                        collection={createListCollection({ items: whiteLabelType })}
                        onValueChange={handleWhiteLabelTypeChange}
                        value={formData.white_label_type ?? undefined}
                        width="100%"
                        positioning={{ sameWidth: true }}
                    >
                        <Select.HiddenSelect />
                        <Select.Control width="100%">
                            <Select.Trigger width="100%">
                                <Select.ValueText placeholder="Select Label Type" />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator />
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                            <Select.Positioner>
                                <Select.Content width="100%">
                                    {whiteLabelType.map((c) => (
                                        <Select.Item item={c} key={c.value} width="100%">
                                            {c.label}
                                            <Select.ItemIndicator />
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Portal>
                    </Select.Root>
                    {formErrors.white_label_type && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.white_label_type}</Text>}
                </Field>
            </Grid>

            <Text fontSize="lg" fontWeight="semibold" mt={8} mb={4}>LOGO</Text>
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                <Box display="flex" flexDirection="column" gap={4}>
                    <Field borderWidth={1} padding={2} label="Light Mode Logo">
                        <Input
                            name="light_mode_logo"
                            type="file"
                            border="none"
                            ref={lightLogoInputRef}
                            onChange={(e) => handleLogoChange(e, "light_mode_logo")}
                        />
                        {formErrors.light_mode_logo && (
                            <Text fontSize="xs" color="red.500" mt={1}>{formErrors.light_mode_logo}</Text>
                        )}
                    </Field>

                    <Field borderWidth={1} padding={2} label="Dark Mode Logo">
                        <Input
                            name="dark_mode_logo"
                            type="file"
                            border="none"
                            ref={darkLogoInputRef}
                            onChange={(e) => handleLogoChange(e, "dark_mode_logo")}
                        />
                        {formErrors.dark_mode_logo && (
                            <Text fontSize="xs" color="red.500" mt={1}>{formErrors.dark_mode_logo}</Text>
                        )}
                    </Field>
                </Box>

                {lightLogoPreview && <LogoPreview title="Light Mode Preview" uri={lightLogoPreview} />}
                {darkLogoPreview && <LogoPreview title="Dark Mode Preview" uri={darkLogoPreview} />}
            </Grid>

            <Box mt={6} pb={6} bg="white">
                <Flex gap={6}>
                    <Button
                        onClick={addWhitelabel}
                    >
                        ADD WHITELABEL
                    </Button>
                    <Button onClick={handleResetAll}>RESET</Button>
                </Flex>
            </Box>
        </Box>
    );
};

const LogoPreview = ({ title, uri }) => (
    <Box p={2} shadow="md" borderWidth={1} display="flex" flexDirection="column">
        <Text fontSize="sm" color="gray.600" mb={1}>{title}</Text>
        <Box
            border="1px"
            borderColor="gray.300"
            rounded="md"
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="white"
        >
            <Image src={uri} alt={title} maxH="8rem" objectFit="contain" />
        </Box>
    </Box>
);